import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import UnknownFaceAvatar from '../../components/UnknownFaceAvatar';
import { Row, Col, Card, Tag, Space, Typography, List, Statistic, Badge, Divider, DatePicker } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  SmileOutlined,
  RiseOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ClockCircleOutlined,
  FireOutlined,
  LineChartOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Doughnut, Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import api from '../../utils/api';
import { EMOTION_COLORS } from '../../appConst';
import { formatDateTime } from '../../utils/helpers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend,
  Filler
);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DashboardContainer = styled.div`
  .dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;
  }

  .auto-refresh-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #52c41a;
    font-size: 12px;
  }
`;

const StyledCard = styled(Card)`
  border-radius: 8px !important;
  border: 1px solid #f0f0f0 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
  transition: all 0.3s ease !important;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  }

  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const StatCard = styled(motion.div)`
  background: #ffffff;
  border-radius: 8px;
  padding: 24px;
  border-left: 4px solid ${props => props.$borderColor || '#B4A0F7'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .stat-icon {
    font-size: 28px;
    color: ${props => props.$borderColor || '#B4A0F7'};
    margin-bottom: 12px;
  }

  .stat-value {
    font-size: 36px;
    font-weight: 600;
    color: #262626;
    margin: 8px 0;
    line-height: 1;
  }

  .stat-label {
    font-size: 14px;
    color: #8c8c8c;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .stat-change {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 12px;
    font-size: 13px;
    color: #595959;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
  }
`;

const InsightCard = styled(Card)`
  border-radius: 8px;
  background: #ffffff;
  border-left: 4px solid #B4A0F7;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .insight-item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 0;

    .anticon {
      font-size: 24px;
      color: #B4A0F7;
    }

    .insight-content {
      flex: 1;

      .insight-label {
        font-size: 12px;
        color: #8c8c8c;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 4px;
      }

      .insight-value {
        font-size: 20px;
        font-weight: 600;
        color: #262626;
      }
    }
  }
`;

const LiveTicker = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 24px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;

  .ticker-icon {
    font-size: 24px;
    color: #B4A0F7;
  }

  .ticker-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 24px;

    .ticker-item {
      display: flex;
      align-items: center;
      gap: 8px;

      .ticker-label {
        font-size: 13px;
        color: #8c8c8c;
        font-weight: 500;
      }

      .ticker-value {
        font-size: 18px;
        font-weight: 600;
        color: #262626;
      }
    }
  }
`;

const Dashboard = () => {
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState({
    totalEmployees: 0,
    unknownFaces: 0,
    totalDetections: 0,
    activeToday: 0,
  });
  const [changes, setChanges] = useState({
    active_today: 0,
    unknown_today: 0,
    detections_today: 0,
    new_employees_this_month: 0,
  });
  const [emotionData, setEmotionData] = useState({
    labels: [],
    datasets: [{ data: [], backgroundColor: Object.values(EMOTION_COLORS), borderWidth: 0 }],
  });
  const [hourlyHeatmap, setHourlyHeatmap] = useState({
    labels: [],
    datasets: [
      {
        label: 'Detections by Hour',
        data: [],
        backgroundColor: 'rgba(180, 160, 247, 0.6)',
        borderColor: '#B4A0F7',
        borderWidth: 1,
      },
    ],
  });
  const [comparativeData, setComparativeData] = useState(null);
  const [quickInsights, setQuickInsights] = useState(null);
  const [recentUnknown, setRecentUnknown] = useState([]);
  const [employeesByEmotion, setEmployeesByEmotion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [dateRange, setDateRange] = useState([dayjs().subtract(6, 'days'), dayjs()]);
  const [heatmapMode] = useState('today'); // Always use today mode

  // Memoize isAdmin result to create stable dependency
  const isAdminUser = useMemo(() => isAdmin(), [isAdmin]);

  const fetchAllDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      // Admin uses date range, users use last 7 days (to show meaningful data)
      const startDate = isAdminUser ? dateRange[0].format('YYYY-MM-DD') : dayjs().subtract(6, 'days').format('YYYY-MM-DD');
      const endDate = isAdminUser ? dateRange[1].format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');

      console.log('Fetching dashboard data:', { startDate, endDate, isAdmin: isAdminUser });

      const [
        analyticsData,
        unknownData,
        heatmapData,
        comparativeAnalysisData,
        insightsData,
        emotionEmployeesData,
      ] = await Promise.all([
        api.getDashboardAnalytics(startDate, endDate).catch(err => {
          console.error('Error fetching analytics:', err);
          return { stats: { total_employees: 0, unknown_faces: 0, total_detections: 0, active_today: 0 }, changes: {}, emotion_distribution: [], daily_trends: [] };
        }),
        api.getUnknownList({ limit: 5, offset: 0 }).catch(err => {
          console.error('Error fetching unknown list:', err);
          return { data: [] };
        }),
        api.getHourlyHeatmap(startDate, endDate, heatmapMode).catch(err => {
          console.error('Error fetching hourly heatmap:', err);
          return { hourly_data: [] };
        }),
        api.getComparativeAnalysis(startDate, endDate).catch(err => {
          console.error('Error fetching comparative analysis:', err);
          return null;
        }),
        api.getQuickInsights().catch(err => {
          console.error('Error fetching quick insights:', err);
          return null;
        }),
        isAdminUser ? api.getEmployeesByEmotion(dayjs().format('YYYY-MM-DD')).catch(err => {
          console.error('Error fetching employees by emotion:', err);
          return null;
        }) : Promise.resolve(null),
      ]);

      console.log('Analytics data received:', analyticsData);

      // Update basic stats
      setStats({
        totalEmployees: analyticsData.stats.total_employees,
        unknownFaces: analyticsData.stats.unknown_faces,
        totalDetections: analyticsData.stats.total_detections,
        activeToday: analyticsData.stats.active_today,
        detectionsToday: analyticsData.stats.detections_today,
        unknownToday: analyticsData.stats.unknown_today,
      });
      setChanges(analyticsData.changes);

      // Update emotion distribution
      if (analyticsData.emotion_distribution && analyticsData.emotion_distribution.length > 0) {
        const emotions = analyticsData.emotion_distribution;
        setEmotionData({
          labels: emotions.map(e => e.emotion),
          datasets: [
            {
              data: emotions.map(e => e.count),
              backgroundColor: emotions.map(e => EMOTION_COLORS[e.emotion] || '#D4D4D4'),
              borderWidth: 0,
            },
          ],
        });
      }

      // Trend data processing removed (unused)

      // Update hourly heatmap
      if (heatmapData.hourly_data && heatmapData.hourly_data.length > 0) {
        const hours = Array.from({ length: 24 }, (_, i) => i);
        const hourCounts = hours.map(hour => {
          const found = heatmapData.hourly_data.find(d => d.hour === hour);
          return found ? found.count : 0;
        });

        setHourlyHeatmap({
          labels: hours.map(h => `${h}:00`),
          datasets: [
            {
              label: 'Detections by Hour',
              data: hourCounts,
              backgroundColor: 'rgba(180, 160, 247, 0.6)',
              borderColor: '#B4A0F7',
              borderWidth: 1,
            },
          ],
        });
      }

      // Emotion trends data processing removed (unused)

      // Update comparative
      console.log('Comparative Analysis Data received:', comparativeAnalysisData);
      if (comparativeAnalysisData) {
        setComparativeData(comparativeAnalysisData);
        console.log('comparativeData state updated');
      } else {
        console.error('Comparative analysis data is null');
      }

      // Update quick insights
      setQuickInsights(insightsData);

      // Update employees by emotion
      setEmployeesByEmotion(emotionEmployeesData);

      // Update recent unknown
      setRecentUnknown(unknownData.data);

      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, isAdminUser, heatmapMode]);

  useEffect(() => {
    fetchAllDashboardData();

    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchAllDashboardData();
    }, 60000);

    return () => clearInterval(interval);
  }, [fetchAllDashboardData]);

  const handleDateRangeChange = (dates) => {
    if (dates) {
      setDateRange(dates);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const multiLineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <DashboardContainer>
      {/* Header */}
      <div className="dashboard-header">
        <div className="auto-refresh-indicator">
          <Badge status="processing" />
          <Text type="secondary">Auto-refresh: {dayjs(lastUpdate).tz('Asia/Kolkata').format('hh:mm:ss A')}</Text>
        </div>
        {isAdmin() && (
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            allowClear={false}
            disabledDate={(current) => current && current > dayjs().endOf('day')}
          />
        )}
      </div>

      {/* Live Activity Ticker */}
      <LiveTicker>
        <div className="ticker-icon">
          <FireOutlined />
        </div>
        <div className="ticker-content">
          <div className="ticker-item">
            <Text className="ticker-label">Live Now:</Text>
            <Text className="ticker-value">{stats.activeToday} Active</Text>
          </div>
          {isAdmin() && (
            <div className="ticker-item">
              <Text className="ticker-label">Today's Detections:</Text>
              <Text className="ticker-value">{stats.detectionsToday || 0}</Text>
            </div>
          )}
          {quickInsights && (
            <div className="ticker-item">
              <Text className="ticker-label">Peak Hour:</Text>
              <Text className="ticker-value">{quickInsights.peak_hour}</Text>
            </div>
          )}
        </div>
      </LiveTicker>

      {/* Statistics Cards */}
      {isAdmin() ? (
        // Admin View - Full Dashboard with all cards
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <StatCard
              $borderColor="#B4A0F7"
              whileHover={{ scale: 1.01 }}
            >
              <div className="stat-icon">
                <TeamOutlined />
              </div>
              <div className="stat-value">{stats.totalEmployees}</div>
              <div className="stat-label">Total Employees</div>
              <div className="stat-change">
                {changes.new_employees_this_month > 0 ? <ArrowUpOutlined /> : changes.new_employees_this_month < 0 ? <ArrowDownOutlined /> : null}
                {changes.new_employees_this_month > 0 ? '+' : ''}{changes.new_employees_this_month} this month
              </div>
            </StatCard>
          </Col>

          <Col span={6}>
            <StatCard
              $borderColor="#FFD4A8"
              whileHover={{ scale: 1.01 }}
            >
              <div className="stat-icon">
                <UserOutlined />
              </div>
              <div className="stat-value">{stats.unknownFaces}</div>
              <div className="stat-label">Unknown Faces</div>
              <div className="stat-change">
                {changes.unknown_today > 0 ? <ArrowUpOutlined /> : changes.unknown_today < 0 ? <ArrowDownOutlined /> : null}
                {changes.unknown_today > 0 ? '+' : ''}{changes.unknown_today} today
              </div>
            </StatCard>
          </Col>

          <Col span={6}>
            <StatCard
              $borderColor="#B8E6D5"
              whileHover={{ scale: 1.01 }}
            >
              <div className="stat-icon">
                <RiseOutlined />
              </div>
              <div className="stat-value">{stats.totalDetections}</div>
              <div className="stat-label">Total Detections</div>
              <div className="stat-change">
                {changes.detections_today > 0 ? <ArrowUpOutlined /> : changes.detections_today < 0 ? <ArrowDownOutlined /> : null}
                {changes.detections_today > 0 ? '+' : ''}{changes.detections_today} today
              </div>
            </StatCard>
          </Col>

          <Col span={6}>
            <StatCard
              $borderColor="#A8C5E8"
              whileHover={{ scale: 1.01 }}
            >
              <div className="stat-icon">
                <SmileOutlined />
              </div>
              <div className="stat-value">{stats.activeToday}</div>
              <div className="stat-label">Active Today</div>
              <div className="stat-change">
                {changes.active_today > 0 ? <ArrowUpOutlined /> : changes.active_today < 0 ? <ArrowDownOutlined /> : null}
                {changes.active_today > 0 ? '+' : ''}{changes.active_today} vs yesterday
              </div>
            </StatCard>
          </Col>
        </Row>
      ) : (
        // User View - Simplified Dashboard with 2 cards only
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <StatCard
              $borderColor="#A8C5E8"
              whileHover={{ scale: 1.01 }}
            >
              <div className="stat-icon">
                <TeamOutlined />
              </div>
              <div className="stat-value">{stats.activeToday}</div>
              <div className="stat-label">Active Employees Today</div>
            </StatCard>
          </Col>

          <Col span={12}>
            <StatCard
              $borderColor="#FFD4A8"
              whileHover={{ scale: 1.01 }}
            >
              <div className="stat-icon">
                <UserOutlined />
              </div>
              <div className="stat-value">{stats.unknownToday || 0}</div>
              <div className="stat-label">Unknown Faces Today</div>
            </StatCard>
          </Col>
        </Row>
      )}

      {/* Activity Heatmap & Emotion Trends */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <StyledCard
            title={<Title level={4}><ClockCircleOutlined /> Today's Activity Heatmap</Title>}
            loading={loading}
          >
            <div style={{ height: 300 }}>
              <Bar data={hourlyHeatmap} options={chartOptions} />
            </div>
          </StyledCard>
        </Col>

        <Col span={12}>
          <StyledCard title={<Title level={4}><SmileOutlined /> Today's Emotion Distribution</Title>} loading={loading}>
            <div style={{ height: 350 }}>
              <Doughnut
                data={emotionData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                  },
                }}
              />
            </div>
          </StyledCard>
        </Col>
      </Row>

      {/* Admin Only Sections */}
      {isAdmin() && (
      <>
      {/* Weekly Comparison & Quick Insights */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={16}>
          <StyledCard title={<Title level={4}>Weekly Comparison (Current vs Previous)</Title>} loading={loading}>
            {comparativeData && (() => {
              console.log('Rendering comparative chart with data:', comparativeData);
              return (
              <>
                <Space style={{ marginBottom: 16 }}>
                  <Statistic
                    title="Current Week"
                    value={comparativeData.current_week?.total_detections || 0}
                    prefix={<ArrowUpOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                  />
                  <Statistic
                    title="Previous Week"
                    value={comparativeData.previous_week?.total_detections || 0}
                  />
                  <Statistic
                    title="Change"
                    value={comparativeData.change_percentage}
                    suffix="%"
                    valueStyle={{ color: comparativeData.change_percentage >= 0 ? '#3f8600' : '#cf1322' }}
                    prefix={comparativeData.change_percentage >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  />
                </Space>
                <div style={{ height: 250 }}>
                  <Line
                    data={{
                      labels: (() => {
                        // Use day names from previous week
                        return comparativeData.previous_week.daily_breakdown.map(d => {
                          const date = new Date(d.date);
                          return date.toLocaleDateString('en-US', { weekday: 'short' });
                        });
                      })(),
                      datasets: [
                        {
                          label: 'Current Week',
                          data: (() => {
                            // Fill current week data, padding with 0 for future days
                            const currentData = comparativeData.current_week.daily_breakdown.map(d => d.count);
                            const prevLength = comparativeData.previous_week.daily_breakdown.length;
                            // Pad with 0s to match previous week length
                            while (currentData.length < prevLength) {
                              currentData.push(0);
                            }
                            return currentData;
                          })(),
                          borderColor: '#B4A0F7',
                          backgroundColor: 'rgba(180, 160, 247, 0.1)',
                          fill: false,
                          tension: 0,
                          pointRadius: 8,
                          pointHoverRadius: 10,
                          borderWidth: 3,
                          showLine: true,
                          spanGaps: false,
                        },
                        {
                          label: 'Previous Week',
                          data: comparativeData.previous_week.daily_breakdown.map(d => d.count),
                          borderColor: '#D4D4D4',
                          backgroundColor: 'rgba(212, 212, 212, 0.1)',
                          fill: false,
                          tension: 0,
                          pointRadius: 8,
                          pointHoverRadius: 10,
                          borderWidth: 3,
                          showLine: true,
                          spanGaps: false,
                        },
                      ],
                    }}
                    options={{
                      ...multiLineChartOptions,
                      elements: {
                        line: {
                          borderWidth: 3,
                        },
                        point: {
                          radius: 8,
                          hoverRadius: 10,
                        },
                      },
                      scales: {
                        ...multiLineChartOptions.scales,
                        x: {
                          ...multiLineChartOptions.scales.x,
                          ticks: {
                            autoSkip: false,
                            maxRotation: 0,
                            minRotation: 0,
                          },
                        },
                      },
                    }}
                  />
                </div>
              </>
              );
            })()}
          </StyledCard>
        </Col>

        <Col span={8}>
          <InsightCard title={<Title level={4}>Quick Insights</Title>} loading={loading}>
            {quickInsights && (
              <Space direction="vertical" style={{ width: '100%' }} size={0}>
                <div className="insight-item">
                  <ClockCircleOutlined />
                  <div className="insight-content">
                    <div className="insight-label">Peak Hour</div>
                    <div className="insight-value">{quickInsights.peak_hour}</div>
                  </div>
                </div>
                <Divider style={{ margin: '8px 0' }} />
                <div className="insight-item">
                  <VideoCameraOutlined />
                  <div className="insight-content">
                    <div className="insight-label">Busiest Camera</div>
                    <div className="insight-value">{quickInsights.busiest_camera}</div>
                  </div>
                </div>
                <Divider style={{ margin: '8px 0' }} />
                <div className="insight-item">
                  <LineChartOutlined />
                  <div className="insight-content">
                    <div className="insight-label">Trend</div>
                    <div className="insight-value">
                      {quickInsights.trend_direction === 'up' && <Tag color="success">↑ {quickInsights.trend_percentage}%</Tag>}
                      {quickInsights.trend_direction === 'down' && <Tag color="error">↓ {quickInsights.trend_percentage}%</Tag>}
                      {quickInsights.trend_direction === 'stable' && <Tag color="default">Stable</Tag>}
                    </div>
                  </div>
                </div>
                <Divider style={{ margin: '8px 0' }} />
                <div className="insight-item">
                  <SmileOutlined />
                  <div className="insight-content">
                    <div className="insight-label">Dominant Emotion</div>
                    <div className="insight-value">{quickInsights.dominant_emotion}</div>
                  </div>
                </div>
              </Space>
            )}
          </InsightCard>
        </Col>
      </Row>

      {/* Employees by Emotion */}
      {employeesByEmotion && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <StyledCard
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Title level={4}><SmileOutlined /> Employee Emotions Today</Title>
                  <Space>
                    <Tag color="success">Happy: {employeesByEmotion.summary.happy_count}</Tag>
                    <Tag color="default">Neutral: {employeesByEmotion.summary.neutral_count}</Tag>
                    <Tag color="warning">Sad: {employeesByEmotion.summary.sad_count}</Tag>
                    <Tag color="error">Angry: {employeesByEmotion.summary.angry_count}</Tag>
                  </Space>
                </div>
              }
              loading={loading}
            >
              <Row gutter={[16, 16]}>
                {/* Happy Employees */}
                {employeesByEmotion.employees.happy.length > 0 && (
                  <Col span={6}>
                    <div style={{
                      background: '#f6ffed',
                      border: '1px solid #b7eb8f',
                      borderRadius: '8px',
                      padding: '16px'
                    }}>
                      <Title level={5} style={{ color: '#52c41a', marginBottom: 12 }}>
                        😊 Happy ({employeesByEmotion.employees.happy.length})
                      </Title>
                      <List
                        size="small"
                        dataSource={employeesByEmotion.employees.happy}
                        renderItem={(emp) => (
                          <List.Item style={{ border: 'none', padding: '8px 0' }}>
                            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                              <Text strong>{emp.full_name}</Text>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {emp.emp_id} • {emp.department || 'N/A'}
                              </Text>
                            </Space>
                          </List.Item>
                        )}
                        style={{ maxHeight: '300px', overflowY: 'auto' }}
                      />
                    </div>
                  </Col>
                )}

                {/* Neutral Employees */}
                {employeesByEmotion.employees.neutral.length > 0 && (
                  <Col span={6}>
                    <div style={{
                      background: '#fafafa',
                      border: '1px solid #d9d9d9',
                      borderRadius: '8px',
                      padding: '16px'
                    }}>
                      <Title level={5} style={{ color: '#8c8c8c', marginBottom: 12 }}>
                        😐 Neutral ({employeesByEmotion.employees.neutral.length})
                      </Title>
                      <List
                        size="small"
                        dataSource={employeesByEmotion.employees.neutral}
                        renderItem={(emp) => (
                          <List.Item style={{ border: 'none', padding: '8px 0' }}>
                            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                              <Text strong>{emp.full_name}</Text>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {emp.emp_id} • {emp.department || 'N/A'}
                              </Text>
                            </Space>
                          </List.Item>
                        )}
                        style={{ maxHeight: '300px', overflowY: 'auto' }}
                      />
                    </div>
                  </Col>
                )}

                {/* Sad Employees */}
                {employeesByEmotion.employees.sad.length > 0 && (
                  <Col span={6}>
                    <div style={{
                      background: '#fffbe6',
                      border: '1px solid #ffe58f',
                      borderRadius: '8px',
                      padding: '16px'
                    }}>
                      <Title level={5} style={{ color: '#faad14', marginBottom: 12 }}>
                        😢 Sad ({employeesByEmotion.employees.sad.length})
                      </Title>
                      <List
                        size="small"
                        dataSource={employeesByEmotion.employees.sad}
                        renderItem={(emp) => (
                          <List.Item style={{ border: 'none', padding: '8px 0' }}>
                            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                              <Text strong>{emp.full_name}</Text>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {emp.emp_id} • {emp.department || 'N/A'}
                              </Text>
                            </Space>
                          </List.Item>
                        )}
                        style={{ maxHeight: '300px', overflowY: 'auto' }}
                      />
                    </div>
                  </Col>
                )}

                {/* Angry Employees */}
                {employeesByEmotion.employees.angry.length > 0 && (
                  <Col span={6}>
                    <div style={{
                      background: '#fff1f0',
                      border: '1px solid #ffccc7',
                      borderRadius: '8px',
                      padding: '16px'
                    }}>
                      <Title level={5} style={{ color: '#ff4d4f', marginBottom: 12 }}>
                        😠 Angry ({employeesByEmotion.employees.angry.length})
                      </Title>
                      <List
                        size="small"
                        dataSource={employeesByEmotion.employees.angry}
                        renderItem={(emp) => (
                          <List.Item style={{ border: 'none', padding: '8px 0' }}>
                            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                              <Text strong>{emp.full_name}</Text>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {emp.emp_id} • {emp.department || 'N/A'}
                              </Text>
                            </Space>
                          </List.Item>
                        )}
                        style={{ maxHeight: '300px', overflowY: 'auto' }}
                      />
                    </div>
                  </Col>
                )}
              </Row>
            </StyledCard>
          </Col>
        </Row>
      )}

      {/* Recent Activity */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <StyledCard title={<Title level={4}>Recent Unknown Faces</Title>} loading={loading}>
            <List
              itemLayout="horizontal"
              dataSource={recentUnknown}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <UnknownFaceAvatar
                        unknownId={item.unknown_id}
                        photoIndex={0}
                        size={48}
                        enablePreview={true}
                        style={{ border: '2px solid #E2E8F0' }}
                      />
                    }
                    title={
                      <Space>
                        <Text strong>{item.unknown_id}</Text>
                        <Tag color="orange">Unknown</Tag>
                      </Space>
                    }
                    description={
                      <Text type="secondary">
                        Last seen: {formatDateTime(item.last_seen_today)}
                        <br />
                        Camera: {item.last_seen_camera_zone}
                      </Text>
                    }
                  />
                </List.Item>
              )}
            />
          </StyledCard>
        </Col>
      </Row>
      </>
      )}
    </DashboardContainer>
  );
};

export default Dashboard;
